import * as React from 'react';
import styled from 'styled-components';
import {transparentize} from 'polished';
import {FileObject, FileType, getFilePath} from 'api/library';
import {LibraryMode} from 'components/Library';
import RoundCheckbox, {RoundCheckboxProps} from 'components/RoundCheckbox';
import ControlButton from 'components/ControlButton';
import {IconAudioType, IconDocumentType, IconTrash, IconVideoType} from 'icons';
import outline from 'helpers/outline';
import {transition} from 'helpers/css';
import trimString from 'helpers/trim-string';
import useImageViewer from "hooks/use-image-viewer";
import {openInNewTab} from "helpers/navigate-to";
import useModal from 'hooks/use-modal';
import VideoModal from "./modals/VideoModal";

export type FileCardProps = FileObject & {
  mode: LibraryMode;
  onDelete?(id: FileObject['id']): void;
  onSelect?(file: FileObject): void;
} & Pick<RoundCheckboxProps, 'checked'>;

export type RemoveTime = number | undefined; // milliseconds

export const Padding = styled.div`
  padding: 14px 0;
  &:not(:last-child) {
    border-bottom: 1px solid ${({theme}) => theme.variables.borderColor};  
  }
`;

export const Container = styled.div<Pick<FileCardProps, 'mode'>>`
  cursor: ${({mode}) => mode === LibraryMode.Picker && 'pointer'};
  gap: 16px;
  grid-template-columns: 1fr min-content;
  align-items: center;
  grid-auto-flow: column;
  display: grid;
  
  ${transition()};
  
  &:focus-visible {
    ${outline(8, 2)};
    border-radius: 5px;
  }
`;

export const Content = styled.div<{
  removeTime: RemoveTime;
}>`
  display: grid;
  grid-template-columns: min-content 1fr;
  align-items: center;
  column-gap: 12px;
  
  ${transition()};
  opacity: ${({removeTime}) => removeTime !== undefined && '.3'};
  
  @media screen and (min-width: 768px) {
    column-gap: 20px;
  }
`;

export const Preview = styled.div`
  position: relative;
  width: 64px;
  height: 64px;
  border-radius: 5px;
  overflow: hidden;
  grid-row: span 2;
  align-items: center;
  justify-content: center;
  display: grid;
  background: ${({theme}) => theme.variables.accentBackgroundColor};
  vertical-align: middle;
`;

export const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const Title = styled.span`
  font-weight: 600;
  user-select: none;
`;

export const Tagline = styled.span`
  color: ${({theme}) => transparentize(.6, theme.variables.color)};
  font-size: 15px;
  user-select: none;
`;

export const CancelRemove = styled.button`
  padding: 6px 8px;
  display: inline-grid;
  text-align: right;

  border-radius: 5px;
  ${transition()};
  
  &:focus {
    ${outline(6, 2)};
  }
`;

export const Controls = styled.div`
  
`;

export const BasePreviewBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  cursor: pointer;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  color: #416ff0;
  transition: color .3s;

  &::before {
    content: '';
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    pointer-events: none;
    z-index: 0;
    opacity: 0;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    transition: opacity .3s;
  }

  &:hover {
    &::before {
      opacity: 1;
    }
    
    color: white;
  }

  svg {
    position: relative;
    z-index: 1;
  }
`

const ImageBox = styled(BasePreviewBox)`
  &::before {
    background-color: rgba(0, 0, 0, .5);
    background-image: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCI+PGcgc3Ryb2tlPSIjZmZmIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCI+PHBhdGggZD0iTTEgMTlsNi02Ii8+PHBhdGggZD0iTTkgOGg2Ii8+PHBhdGggZD0iTTEyIDV2NiIvPjwvZz48Y2lyY2xlIGN4PSIxMiIgY3k9IjgiIHI9IjciIGZpbGw9Im5vbmUiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLXdpZHRoPSIyIi8+PC9zdmc+');
    background-repeat: no-repeat;
    background-position: center;
  }
`;

/**
 * This component renders the file card
 * @param {FileCardProps} props
 * @return {React.ReactElement<FileCardProps>}
 * @constructor
 */
export default function FileCard(
    props: FileCardProps,
): React.ReactElement<FileCardProps> {
  const videoModal = useModal();
  const {openImageViewer} = useImageViewer();

  function handleOpenFile(e: React.MouseEvent) {
    e.preventDefault();

    switch (props.type) {
      case FileType.Video:
        videoModal.setModalVisible(true)
        break;
      case FileType.Image:
        openImageViewer(getFilePath(props.id))
        break;
      case FileType.Document
        || FileType.Audio
        || FileType.External:
            const url = getFilePath(props.id);
            url && openInNewTab(url)
            break;
    }
  }

  const [
    removeTime,
    setRemoveTime,
  ] = React.useState<RemoveTime>(undefined);

  React.useEffect(() => {
    if (removeTime !== undefined) {
      const interval = setInterval(() => {
        setRemoveTime(removeTime - 1000);
        if (removeTime <= 0) {
          handleCancelRemove();
          props.onDelete && props.onDelete(props.id);
        }
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [removeTime]);

  /**
   * Get preview image
   * @return {JSX.Element}
   */
  function getPreview() {
    /**
     * Get file icon
     * @param {FileType} type
     * @return {JSX.Element}
     */
    function getFileIcon(type: FileType) {
      switch (type) {
        case FileType.Audio: return (
          <BasePreviewBox>
            <IconAudioType />
          </BasePreviewBox>
        );
        case FileType.External:
        case FileType.Document: return (
          <BasePreviewBox>
            <IconDocumentType />
          </BasePreviewBox>
        );
        case FileType.Video: return (
          <BasePreviewBox>
            <IconVideoType />
          </BasePreviewBox>
        );
        default: return undefined;
      }
    }
    return props.type === FileType.Image
      ? (
          <ImageBox>
            <Image
              src={getFilePath(props.id)}
              alt="Изображение файла"
            />
          </ImageBox>
        )
      : getFileIcon(props.type);
  }

  /**
   * Get the title
   * @param {number} length
   * @return {string}
   */
  function getTitle(length: number = 50): string | undefined {
    return props.title ? trimString(props.title, length) : (
      trimString(props.uri, length)
    );
  }

  /**
   * Get the tagline
   * @return {string}
   */
  function getTagline(): string {
    /**
     * Convert the size to kilobyte
     * @return {string}
     */
    function covertSizeToKilobyte(): string | undefined {
      return props.meta && Math.floor(props.meta.size / 1000)
          .toFixed(0);
    }
    const size = covertSizeToKilobyte();
    return size !== undefined ? (
      `${size} КБ`
    ) : trimString(props.uri, 60);
  }

  /**
   * Get remove label
   * @return {string | undefined}
   */
  function getRemoveLabel(): string | undefined {
    return removeTime !== undefined ? (
      `Отменить(${removeTime / 1000})`
    ) : undefined;
  }

  /**
   * The handle click on remove control
   */
  function handleRemove(): void {
    setRemoveTime(5000);
  }

  /**
   * The handle click on cancel remove control
   */
  function handleCancelRemove(): void {
    setRemoveTime(undefined);
  }

  /**
   * Render the controls
   * @return {JSX.Element}
   */
  function renderControls() {
    /**
     * Handle select the file
     */
    function handleSelect(): void {
      props.onSelect && props.onSelect({
        id: props.id,
        type: props.type,
        title: props.title,
        uri: props.uri,
        url: props.url, // todo: Эта штука зависит от типа файла.
        userID: props.userID,
        meta: props.meta,
      });
    }
    switch (props.mode) {
      case LibraryMode.Viewer: return (
        <ControlButton onClick={handleRemove}>
          <IconTrash />
        </ControlButton>
      );
      case LibraryMode.Picker: return (
        <RoundCheckbox
          checked={props.checked}
          onChange={handleSelect}
        />
      );
    }
  }

  const Wrapper = props.mode === LibraryMode.Picker ? (
    'label'
  ) : (
    'div'
  );

  return (
    <Wrapper>
      <Padding>
        <Container mode={props.mode} tabIndex={0}>
          <Content removeTime={removeTime}>
            <Preview onClick={handleOpenFile}>
              {getPreview()}
            </Preview>
            <Title>
              {getTitle()}
            </Title>
            <Tagline>
              {getTagline()}
            </Tagline>
          </Content>
          {removeTime !== undefined ? (
            <CancelRemove onClick={handleCancelRemove}>
              {getRemoveLabel()}
            </CancelRemove>
          ) : (
            <Controls>
              {renderControls()}
            </Controls>
          )}
        </Container>
      </Padding>
      {videoModal.isModalVisible && (
        <VideoModal
          title={props.title}
          src={getFilePath(props.id)}
          onBlur={() => videoModal.setModalVisible(false)}
        />
      )}
    </Wrapper>
  );
}
